/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { GlobeAltIcon, PlusCircleIcon, SearchIcon, SwitchHorizontalIcon } from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import {
    Badge,
    Box,
    Bubble,
    Button,
    ButtonGroup,
    Card,
    FrameInset,
    Pagination,
    Tabs,
    Theme
} from '@nexigendigital/nxui'
import { useDomainListQuery, useDomainQuery } from 'api/domain'
import { Meta } from 'models/app'
import { Domain as DomainModel } from 'models/domain'
import { getServiceStatusColour, uid } from 'helpers/utils'

import Search from 'components/search/search'
import Domain from 'containers/domains/domain'
import Overview from 'containers/domains/overview'
import Information from 'containers/domains/information'
import Contacts from 'containers/domains/contacts'
import Dns from 'containers/domains/dns'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Domains() {
    const navigate = useNavigate()
    const location = useLocation()
    const [meta, setMeta] = useState<Meta>({
        page: 1,
        filter_by: undefined,
        sort_by: undefined,
        search_by: undefined
    })
    const domainId = +location.pathname.split('/')[2]
    const { isLoading, isSuccess, data: domains } = useDomainListQuery(meta)
    useDomainQuery(domainId, { skip: !domainId })
    const [reset, setReset] = useState(false)
    const columns = useMemo(() => ([{
        Header: 'Domains',
        accessor: 'domains'
    }]), [])
    const domainsData = useMemo(() => {
        if (!domains) {
            return []
        }

        return domains.data.map(({ id, domain, status }: DomainModel) => {
            const domainName = domain.split('.')[0]
            const domainTLD = domain.split('.').slice(1).toString().replace(',', '.')

            return {
                domains: (
                    <DomainItem key={`${uid()}-${domain}`} onClick={() => {
                        navigate(`/domains/${id}`)
                        setReset(true)
                    }}>
                        <li>
                            <div>
                                <span>{domainName}<strong>.{domainTLD}</strong></span>
                            </div>
                            <Badge color={getServiceStatusColour(status)}>{status}</Badge>
                        </li>
                    </DomainItem>
                )
            }
        })
    }, [domains])

    const filterable = useMemo(() => {
        if (domains?.meta) {
            const filter = Object.entries(domains?.meta.filterable)
            const [filterByKey, filterByValue] = filter.find((item) => item.includes('status')) as [key: string, value: object]

            return Object.values(filterByValue).map((status: string) => {
                return {
                    type: 'button',
                    label: status.toUpperCase(),
                    onClick: () => setMeta({ ...meta, filter_by: { key: filterByKey, value: status } })
                }
            })
        }
    }, [domains])

    const searchByName = useMemo(() => {
        if (domains?.meta) {
            const { base } = domains?.meta.searchable

            return `-${Object.keys(base).find((column) => column === 'name')}` || ''
        }

        return ''
    }, [domains])

    useEffect(() => {
        if (isSuccess && !domainId && location.pathname === '/domains') {
            navigate(`/domains/${domains.data[0].id}`)
        }
    }, [isSuccess, domainId, location])

    useEffect(() => {
        const timer = setTimeout(() => {
            setReset(false)
        }, 500)

        return () => clearTimeout(timer)
    }, [domainId])

    return (
        <FrameInset
            inset={'split'}
            location={{ pathname: '/Domains' }}
            left={{
                render: (
                    <DomainsWrapper>
                        <Bubble
                            title={'Active Domains'}
                            desc={Array.isArray(domains?.data) ? domains.data.length : 0}
                            icon={<GlobeAltIcon />}
                        />
                        <Box>
                            {domains?.meta &&
                                <TabsWrapper tabs={[
                                    {
                                        icon: SearchIcon,
                                        label: 'Search',
                                        render: (
                                            <ContentWrapper>
                                                <Search searchKey={searchByName} meta={meta} setMeta={setMeta} />
                                            </ContentWrapper>
                                        )
                                    },
                                    {
                                        icon: SearchIcon,
                                        label: 'Refine',
                                        render: (
                                            <ContentWrapper>
                                                <ButtonGroupWrapper
                                                    color={'secondary'}
                                                    list={filterable}
                                                />
                                            </ContentWrapper>
                                        )
                                    }
                                ]} />
                            }
                            <Card
                                table={{
                                    conditions: {
                                        loading: isLoading && 'Loading Domains...',
                                        search: false,
                                        pagination: false
                                    },
                                    render: {
                                        columns: () => columns,
                                        data: () => domainsData
                                    }
                                }}
                                footer={(
                                    <Pagination
                                        currentPage={domains?.meta.current_page || 1}
                                        totalCount={domains?.meta.total || 1}
                                        pageSize={domains?.meta.per_page || 15}
                                        onPageChange={(page: number) => setMeta({ ...meta, page })}
                                    />
                                )}
                            />
                        </Box>
                        <Links>
                            <Button
                                color={'primary'}
                                onClick={() => navigate('/shop')}
                            >
                                Purchase New Service
                            </Button>
                            <Button color={'flat'} onClick={() => navigate('/account')}>
                                Account
                            </Button>
                        </Links>
                    </DomainsWrapper>
                )
            }}
            right={reset
                ? null
                : {
                    when: location.key,
                    render: (
                        <DomainWrapper>
                            <Domain>
                                <Overview />
                                <Config tabs={[
                                    {
                                        icon: PlusCircleIcon,
                                        label: 'General',
                                        render: <Information />
                                    },
                                    {
                                        icon: PlusCircleIcon,
                                        label: 'Contacts',
                                        render: <Contacts />
                                    },
                                    {
                                        icon: SwitchHorizontalIcon,
                                        label: 'DNS & Nameservers',
                                        render: <Dns />
                                    }
                                ]} />
                            </Domain>
                        </DomainWrapper>
                    )
                }}
        />
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const DomainsWrapper = styled.section`
    width: 100%;
    display: grid;
    gap: 16px;

    article {
        width: 100%;
        margin-bottom: 0;
    }

    td {
        padding: 0;
    }
`
const Links = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;

    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
        flex-direction: column;
        gap: 10px;
    }

    > button {
        width: 47%;

        ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
            width: 100%;
            margin: 0;
        }
    }
`

const DomainWrapper = styled.section`
    height: 100%;
    width: 100%;
`

const DomainItem = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 9px;

    > li {
        display: grid;
        gap: 9px;
        padding: 18px;

        &:hover {
            cursor: pointer;
        }
    }
`

const Config = styled(Tabs)`
    article {
        margin-bottom: 0;
        border-radius: 0;
    }
`

const ButtonGroupWrapper = styled(ButtonGroup)`
    max-width: 400px;
    display: flex;
    flex-wrap: wrap;
`

const TabsWrapper = styled(Tabs)`
    width: 100%;
    margin-bottom: 0;
    border-radius: 0;
    box-shadow: none;
`

const ContentWrapper = styled.div`
    padding: 0 20px 20px;
`
